import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import styles from './EditJob.module.css';
import FormField from '../../../components/UI/UpdatedFormUI/FormField/FormField';
import FormSelectField from '../../../components/UI/UpdatedFormUI/FormSelectField/FormSelectField';

import BubbleButton from '../../../components/UI/Buttons/BubbleButton/BubbleButton';
import FormOptionField from '../../../components/UI/UpdatedFormUI/FormOptionField/FormOptionField';
import { ExclusiveJobIcon } from '../../../assets/images/icons';
import { useGetJobCategoriesHook, useGetCities, useGetRoleBasedSkillsHook } from '../../../utils/utilityHooks';


function JobOverView({ formik, setActiveSteps, activeSteps, isEditMode = false, saveAsDraftOnly, updateJobOnly, id, draftLoading, submitLoading, formik_values_status }) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [formValuesInitialized, setFormValuesInitialized] = useState(false);
    const categoryOptions = useGetJobCategoriesHook() || [];
    const citiesOptions = useGetCities(formik.values.country) || [];
    
    // Job title suggestions
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [titleSuggestions, setTitleSuggestions] = useState([]);
    const [titleInput, setTitleInput] = useState(formik.values.title || '');
    const jobTitleOptions = useGetRoleBasedSkillsHook() || [];

    const [localDraftLoading, setLocalDraftLoading] = useState(false);
    const [localUpdateLoading, setLocalUpdateLoading] = useState(false);

    // Track when form values are initialized
    useEffect(() => {
        if (!formValuesInitialized && (formik.values.title || formik.values.category)) {
            setFormValuesInitialized(true);
            setTitleInput(formik.values.title || '');
        }
    }, [formik.values, formValuesInitialized]);

    // Manage loading state based on data availability
    useEffect(() => {
        // Only manage loader in edit mode
        if (isEditMode) {
            // Only hide loader when we have form values AND options data
            if (formValuesInitialized && categoryOptions?.length > 0 && citiesOptions?.length > 0) {
                // Small delay to ensure UI renders all form fields properly
                const timer = setTimeout(() => {
                    setIsLoading(false);
                }, 300);
                return () => clearTimeout(timer);
            }
        } else {
            // For new job creation, always set loading to false
            setIsLoading(false);
        }
    }, [isEditMode, formValuesInitialized, categoryOptions, citiesOptions, formik.values.country]);

    // Handle title input change and show suggestions
    const handleTitleInputChange = (e) => {
        const value = e.target.value || '';
        formik.setFieldValue('title', value);
        setTitleInput(value);
        
        if (value.trim()) {
            // Filter job title options that match the input
            const filteredOptions = jobTitleOptions?.filter(option => 
                option?.label?.toLowerCase().includes(value.toLowerCase())
            ) || [];
            setTitleSuggestions(filteredOptions.slice(0, 5)); // Limit to top 5 matches
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    // Handle selecting a title from suggestions
    const handleSelectTitle = (title) => {
        if (!title) return;
        
        formik.setFieldValue('title', title.label || '');
        setTitleInput(title.label || '');
        setShowSuggestions(false);
    };

    // Handle category selection with logging
    const handleCategoryChange = (e) => {
        const categoryValue = e.target.value;
        formik.handleChange(e);
        
        // Log selected category information
        const selectedCategory = categoryOptions?.find(cat => cat?.value === categoryValue);
        if (selectedCategory) {
            
            // Store the category name in local storage for debugging
            try {
                localStorage.setItem('selectedCategoryName', selectedCategory.label || '');
                localStorage.setItem('selectedCategoryId', selectedCategory.value || '');
            } catch (error) {
                console.error("Error storing category in localStorage:", error);
            }
        }
    };

    const jobLocationOptions = [
        { value: 'remote', label: t('forms.jobLocation.remote') },
        { value: 'in_person', label: t('forms.jobLocation.in_person') },
        { value: 'hybrid', label: t('forms.jobLocation.hybrid') },
    ];
    const jobTypeOptions = [
        { value: 'full_time', label: t('general.UI.full_time') },
        { value: 'part_time', label: t('general.UI.part_time') },
        { value: 'temporary', label: t('forms.workExperience.employment_status.temporary') },
        { value: 'contract', label: t('forms.workExperience.employment_status.contract') },
        { value: 'internship', label: t('forms.workExperience.employment_status.internship') },
    ];

    if (isLoading) {
        return (
            <div className={styles.loaderContainer}>
                <div className={styles.loader}></div>
                <p>{t('forms.login.processing')}</p>
            </div>
        );
    }

    return (
        <>
            <div className={styles.header}>
                <div className={styles.headerButton}>
                    <FormOptionField
                        name="show_verified_jobSeeker"
                        type="checkbox"
                        options={[
                            {
                                value: true,
                                label: <p className={styles.buttonTitle}>{t('jobOverView.Exclusive_Job')}</p>
                            },
                        ]}
                        onChange={() => formik.setFieldValue("exlusiveJob", !formik.values.exlusiveJob)} // Toggle value manually
                        value={formik.values.exlusiveJob}
                        error={formik.touched.exlusiveJob && formik.errors.exlusiveJob}
                    />
                    <img src={ExclusiveJobIcon} className={styles.verifyIcon} />
                </div>
                <p className={`${styles.headerDescription} ${styles.margin}`}>{t('jobOverView.description')}</p>
            </div>
            <div className={`${styles.row} ${styles.marginFullScreen}`} >
                <div className={styles.inputContainer}>
                    <div className={styles.titleInputContainer}>
                        <FormField
                            name="title"
                            type="text"
                            label={t('jobOverView.title')[0].toUpperCase() + t('jobOverView.title').slice(1, t('jobOverView.title').length)}
                            placeholder={t(`jobOverView.titlePlaceHolder`)}
                            hasBorder
                            onChange={handleTitleInputChange}
                            onBlur={() => {
                                // Keep suggestions open for a moment to allow clicking
                                setTimeout(() => setShowSuggestions(false), 200);
                                formik.handleBlur({ target: { name: 'title' } });
                            }}
                            value={titleInput}
                            error={formik.touched.title && formik.errors.title}
                        />
                        {showSuggestions && titleSuggestions && titleSuggestions.length > 0 && (
                            <div className={styles.suggestionsList}>
                                {titleSuggestions.map(title => title && (
                                    <div 
                                        key={title.id || Math.random()} 
                                        className={styles.suggestionItem}
                                        onClick={() => handleSelectTitle(title)}
                                    >
                                        {title.label || ''}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.inputContainer}>
                    <FormSelectField
                        name="category"
                        label={t(`jobs.category`)}
                        placeholder={t('general.UI.select')}
                        type="default"
                        hasBorder
                        options={categoryOptions}
                        onFormikChange={handleCategoryChange}
                        value={formik.values.category}
                        error={formik.touched.category && formik.errors.category}
                    />
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.inputContainer}>
                    <FormSelectField
                        name="country"
                        type="country"
                        label={t('job.country')}
                        placeholder={t('general.UI.select')}
                        hasBorder
                        options={['QA', 'AE', 'SA', 'BH', 'KW', 'OM']}
                        onFormikChange={formik.handleChange}
                        value={formik.values.country}
                        error={formik.touched.country && formik.errors.country}
                    />
                </div>
                <div className={styles.inputContainer}>
                    <div>
                        <FormSelectField
                            name="city"
                            type="default"
                            label={t('jobOverView.City')}
                            hasBorder
                            options={citiesOptions}
                            onFormikChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.city}
                            error={formik.touched.city && formik.errors.city}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.inputContainer}>
                    <FormOptionField
                        name="show_verified_jobSeeker"
                        dark
                        type="checkbox"
                        options={[
                            {
                                value: true,
                                label: <p className={styles.headerDescription}>{t('jobOverView.Specific_Country')}</p>
                            },
                        ]}
                        onChange={() => formik.setFieldValue("specifiedForCountry", !formik.values.specifiedForCountry)} // Toggle value manually
                        value={formik.values.specifiedForCountry}
                        error={formik.touched.specifiedForCountry && formik.errors.specifiedForCountry}
                    />
                </div>
                <div className={styles.inputContainer}>
                    <FormOptionField
                        name="show_verified_jobSeeker"
                        dark
                        type="checkbox"
                        options={[
                            {
                                value: true,
                                label: <p className={styles.headerDescription}>{t('jobOverView.Specific_City')}</p>
                            },
                        ]}
                        onChange={() => formik.setFieldValue("specifiedForCity", !formik.values.specifiedForCity)} // Toggle value manually
                        value={formik.values.specifiedForCity}
                        error={formik.touched.specifiedForCity && formik.errors.specifiedForCity}
                    />
                </div>
            </div>
            <FormOptionField
                name="job_location"
                label={t('jobOverView.location_type')}
                type="radio"
                dark
                options={jobLocationOptions}
                onChange={formik.handleChange}
                value={formik.values.job_location}
                error={formik.touched.job_location && formik.errors.job_location}
            />
            <FormOptionField
                name="job_type"
                label={t('jobOverView.job_type')}
                type="radio"
                dark
                options={jobTypeOptions}
                onChange={formik.handleChange}
                value={formik.values.job_type}
                error={formik.touched.job_type && formik.errors.job_type}
            />
            
            <div style={{ display: "flex", gap: 10, marginTop: "20px" }}>
                <BubbleButton 
                    dark 
                    headerDarkButton
                    type="submit"
                    onClick={() => {
                        if (formik.values.city && formik.values.country && formik.values.category && formik.values.title) {
                            setActiveSteps(activeSteps + 1)
                        }
                    }}
                    style={{ padding: '10px 20px' }}
                >
                    {t('jobOverView.Next')}
                </BubbleButton>
                
                {(!id || (formik_values_status === 'draft')) && (
                    <BubbleButton
                        dark
                        headerDarkButton
                        onClick={async () => {
                            setLocalDraftLoading(true);
                            await saveAsDraftOnly();
                            setLocalDraftLoading(false);
                        }}
                        style={{ padding: '10px 20px', marginLeft: '10px' }}
                        disabled={localDraftLoading || localUpdateLoading || draftLoading || submitLoading}
                    >
                        {localDraftLoading ? (
                            <div className={styles.buttonLoader}>
                                <div className={styles.spinnerSmall}></div>
                                <span>{t('forms.login.processing')}</span>
                            </div>
                        ) : (
                            t('job.save_draft', 'Save Draft')
                        )}
                    </BubbleButton>
                )}
                
                {(id && formik_values_status !== 'draft') && (
                    <BubbleButton
                        dark
                        headerDarkButton
                        onClick={async () => {
                            setLocalUpdateLoading(true);
                            await updateJobOnly();
                            setLocalUpdateLoading(false);
                        }}
                        style={{ padding: '10px 20px', marginLeft: '10px' }}
                        disabled={localDraftLoading || localUpdateLoading || draftLoading || submitLoading}
                    >
                        {localUpdateLoading ? (
                            <div className={styles.buttonLoader}>
                                <div className={styles.spinnerSmall}></div>
                                <span>{t('forms.login.processing')}</span>
                            </div>
                        ) : (
                            t('job.update', 'Update')
                        )}
                    </BubbleButton>
                )}
            </div>
        </>
    );
}

export default JobOverView;